import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import AvatarIcon from '../../icons/AvatarIcon'
import { Button, Intent, Position, Tooltip } from '@blueprintjs/core'
import styles from './style.scss'

const baseStyle = {
  display: 'flex',
  // flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 16,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out',
  width: 'inherit',
  height: 'inherit'
}

const activeStyle = {
  borderColor: '#2196f3'
}

const acceptStyle = {
  borderColor: '#00e676'
}

const rejectStyle = {
  borderColor: '#ff1744'
}

const MaxFileSize = 200000

function DropzoneComponent(props: any) {
  const [files, setFiles] = useState([])
  const [selectedImages, setSelectedImages] = useState(props?.selectedImage || [] as any)
  const imagePath = typeof selectedImages === 'string' && selectedImages ? true : false
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    acceptedFiles.forEach((file: any) => {
      setSelectedImages([file])
      props.botAvatar(file)
    })
  }, [])

  function fileValidator(file) {
    if (file.size > MaxFileSize) {
      return {
        code: 'file-too-large',
        message: `File size must not be larger than ${MaxFileSize/1000}kb.`
      }
    }
    return null
  }

  const { fileRejections, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: { 'image/png': ['.png'], 'image/jpg': ['.jpg'], 'image/jpeg': ['.jpeg'] },
    maxFiles: 1,
    validator: fileValidator
  })

  const fileRejectionItems = fileRejections.map(({ file, errors  }) => {
    return (
      <>
        {errors.map(e => <span style={{color: '#c23030', fontFamily: 'Barlow Medium', paddingTop: '4px'}} key={e.code}>{e.message}</span>)}
      </>
    )
  })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isDragActive, isDragReject, isDragAccept]
  )

  // clean up
  useEffect(
    () => () => {
      if(!imagePath) {
        selectedImages.forEach((file: any) => URL.revokeObjectURL(file.preview))
      }
    },
    [selectedImages]
  )

  const deleteFile = () => {
    setSelectedImages([])
    props.handleDeleteFileName()
  }

  const deletableFile = () => (
    <Tooltip content="delete" position={Position.TOP}>
      <Button className={styles.deleteFile} minimal small intent={Intent.DANGER} icon="trash" onClick={deleteFile} />
    </Tooltip>
  )

  const showDeleteButton = (selectedImages.length > 0) ? true : false
  const sectionStyle = (selectedImages.length > 0) ? {width: '100px', height: '100px'} : {}
  const avatarCoverStyle = (selectedImages.length > 0) ? {width: 'fit-content'}: {}

  return (
    <div className={styles.avatarCover} style={{...{ textAlign: 'center', display: 'flex', flexDirection: 'column'}, ...avatarCoverStyle}}>
      <section style={{...{position: 'relative', cursor: 'pointer'}, ...sectionStyle}}>
        <div {...getRootProps({ style })}>
          <input {...getInputProps() as React.InputHTMLAttributes<HTMLInputElement>} />
          {selectedImages.length === 0 && (
              <div className="drag-file-area">
                {
                  props.customIcon ? props.customIcon : <AvatarIcon style={{ border: '1px solid rgb(200 198 198)', padding: '15px', borderRadius: '50%' }} />
                }
                <h3 className="dynamic-message" style={{ marginBottom: '0px', color: '#666' }}>
                  {' '}
                  Drag & drop or click here{' '}
                </h3>
                <p style={{color: '#666'}}>to upload your image (max {MaxFileSize / 1000}kb).</p>
              </div>
          )}
          <aside style={{ maxWidth: '205px', maxHeight: '156px', overflow: 'hidden'}}>
            {
              imagePath &&
              <img src={selectedImages} width={'100%'} alt={'Your Logo'} />
            }
            {!imagePath && selectedImages.length > 0 ? (
              <div>
                {selectedImages.map((image: Blob | MediaSource, index: React.Key | null | undefined) => (
                  <img src={`${URL.createObjectURL(image)}`} key={index} width={'100%'} alt={'Avatar Image'} />
                ))}
              </div>
            ) : <></>}
          </aside>
        </div>
        {showDeleteButton && (<div className={styles.imageWrapper} style={{position: 'absolute', height: '100%', width: '100%', top: '0', left: '0'}}><div style={{ position: 'absolute', top: '5px', right: '5px' }}>{deletableFile()}</div></div>)}
      </section>
      {fileRejectionItems ? fileRejectionItems : ''}
    </div>
  )
}

export default DropzoneComponent
